import React, { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import { Badge } from '@/components/ui/badge';
import type { TimesheetReportEntry } from '@/types/timesheetReport';
import type { TimesheetStatus } from '@/types/timesheet';
import type { FieldConfig } from '@/components/TimesheetReports/FieldSelector';
import {
  CheckSquare,
  XCircle,
  Clock,
  FileText,
  AlertCircle,
  PoundSterling,
  Timer,
} from 'lucide-react';

interface TimesheetReportGridProps {
  data: TimesheetReportEntry[];
  isManager?: boolean;
  enableOvertimeTracking?: boolean;
  onRowClick?: (entry: TimesheetReportEntry) => void;
  columnVisibility?: Record<string, boolean>;
  fieldConfig?: FieldConfig[];
  groupBy?: string[];
}

// Status configuration
const statusConfig: Record<
  TimesheetStatus,
  { label: string; color: string; icon: React.ElementType }
> = {
  draft: {
    label: 'Draft',
    color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200',
    icon: FileText,
  },
  submitted: {
    label: 'Submitted',
    color: 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-200',
    icon: Clock,
  },
  approved: {
    label: 'Approved',
    color: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200',
    icon: CheckSquare,
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200',
    icon: XCircle,
  },
  recalled: {
    label: 'Recalled',
    color: 'bg-burntorange-100 text-burntorange-800 dark:bg-burntorange-900 dark:text-burntorange-200',
    icon: AlertCircle,
  },
  locked: {
    label: 'Locked',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    icon: CheckSquare,
  },
};

export const TimesheetReportGrid: React.FC<TimesheetReportGridProps> = ({
  data,
  isManager = false,
  enableOvertimeTracking = false,
  onRowClick,
  columnVisibility,
  fieldConfig,
  groupBy,
}) => {
  // Define columns
  const columns: ColumnDef<TimesheetReportEntry>[] = useMemo(() => {
    const baseColumns: ColumnDef<TimesheetReportEntry>[] = [
      {
        accessorKey: 'weekEnding',
        header: 'Week Ending',
        cell: ({ row }) => (
          <button
            onClick={() => onRowClick?.(row.original)}
            className="text-left hover:underline text-info-600 dark:text-info-400 font-medium cursor-pointer"
          >
            {format(row.original.weekEnding, 'MMM d, yyyy')}
          </button>
        ),
        enableGrouping: true,
        aggregationFn: 'count',
        aggregatedCell: ({ getValue }) => `${getValue()} weeks`,
      },
      {
        accessorKey: 'employeeName',
        header: 'Employee',
        enableGrouping: true,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const config = statusConfig[status];
          const Icon = config.icon;
          return (
            <Badge className={config.color}>
              <Icon className="h-3 w-3 mr-1" />
              {config.label}
            </Badge>
          );
        },
        enableGrouping: true,
      },
      {
        accessorKey: 'projectCode',
        header: 'Project Code',
        enableGrouping: true,
      },
      {
        accessorKey: 'projectName',
        header: 'Project Name',
        enableGrouping: true,
      },
      {
        accessorKey: 'costCentreCode',
        header: 'Cost Centre',
        enableGrouping: true,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <div className="max-w-[300px] truncate" title={row.original.description}>
            {row.original.description || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'mondayHours',
        header: 'Mon',
        cell: ({ row }) => (row.original.mondayHours > 0 ? row.original.mondayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'tuesdayHours',
        header: 'Tue',
        cell: ({ row }) => (row.original.tuesdayHours > 0 ? row.original.tuesdayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'wednesdayHours',
        header: 'Wed',
        cell: ({ row }) => (row.original.wednesdayHours > 0 ? row.original.wednesdayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'thursdayHours',
        header: 'Thu',
        cell: ({ row }) => (row.original.thursdayHours > 0 ? row.original.thursdayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'fridayHours',
        header: 'Fri',
        cell: ({ row }) => (row.original.fridayHours > 0 ? row.original.fridayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'saturdayHours',
        header: 'Sat',
        cell: ({ row }) => (row.original.saturdayHours > 0 ? row.original.saturdayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'sundayHours',
        header: 'Sun',
        cell: ({ row }) => (row.original.sundayHours > 0 ? row.original.sundayHours.toFixed(2) : '-'),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => `${(getValue() as number).toFixed(2)} hrs`,
      },
      {
        accessorKey: 'totalHours',
        header: 'Total Hours',
        cell: ({ row }) => (
          <span className="font-semibold">{row.original.totalHours.toFixed(2)}</span>
        ),
        aggregationFn: 'sum',
        aggregatedCell: ({ getValue }) => (
          <span className="font-bold">{(getValue() as number).toFixed(2)} hrs</span>
        ),
      },
      {
        accessorKey: 'billable',
        header: 'Billable',
        cell: ({ row }) =>
          row.original.billable ? (
            <Badge variant="outline" className="gap-1">
              <PoundSterling className="h-3 w-3" />
              Yes
            </Badge>
          ) : (
            <span className="text-muted-foreground">No</span>
          ),
        enableGrouping: true,
      },
      {
        accessorKey: 'hasRejections',
        header: 'Rejections',
        cell: ({ row }) =>
          row.original.hasRejections ? (
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              {row.original.rejectionCount}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
    ];

    // Add department column for managers (after Employee column which is now always visible)
    if (isManager) {
      baseColumns.splice(2, 0, {
        accessorKey: 'department',
        header: 'Department',
        enableGrouping: true,
      });
    }

    // Add overtime columns when overtime tracking is enabled — gated by org setting
    if (enableOvertimeTracking) {
      // Insert OT (Yes/No badge) column after Billable
      const billableIdx = baseColumns.findIndex(
        (col) => (col as { accessorKey: string }).accessorKey === 'billable'
      );
      const insertAt = billableIdx >= 0 ? billableIdx + 1 : baseColumns.length;

      baseColumns.splice(insertAt, 0,
        {
          accessorKey: 'isOvertime',
          header: 'OT',
          cell: ({ row }) =>
            row.original.isOvertime ? (
              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 gap-1">
                <Timer className="h-3 w-3" />
                Yes
              </Badge>
            ) : (
              <span className="text-muted-foreground">No</span>
            ),
          enableGrouping: true,
        },
        {
          accessorKey: 'overtimeTypeName',
          header: 'Overtime Type',
          cell: ({ row }) =>
            row.original.overtimeTypeName ? (
              <span className="text-sm">{row.original.overtimeTypeName}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
          enableGrouping: true,
        }
      );
    }

    // Sort columns based on fieldConfig if provided
    if (fieldConfig && fieldConfig.length > 0) {
      const orderedColumns: ColumnDef<TimesheetReportEntry>[] = [];
      const columnMap = new Map(baseColumns.map(col => [(col as { accessorKey: string }).accessorKey, col]));
      
      // Add columns in the order specified by fieldConfig
      fieldConfig
        .sort((a, b) => a.order - b.order)
        .forEach(field => {
          const column = columnMap.get(field.id);
          if (column) {
            orderedColumns.push(column);
            columnMap.delete(field.id);
          }
        });
      
      // Add any remaining columns that weren't in fieldConfig (shouldn't happen, but just in case)
      columnMap.forEach(column => orderedColumns.push(column));
      
      return orderedColumns;
    }

    return baseColumns;
  }, [isManager, enableOvertimeTracking, onRowClick, fieldConfig]);

  // Field labels for better UX
  const fieldLabels: Record<string, string> = {
    weekEnding: 'Week Ending',
    employeeName: 'Employee',
    department: 'Department',
    status: 'Status',
    projectCode: 'Project Code',
    projectName: 'Project Name',
    costCentreCode: 'Cost Centre',
    description: 'Description',
    mondayHours: 'Monday',
    tuesdayHours: 'Tuesday',
    wednesdayHours: 'Wednesday',
    thursdayHours: 'Thursday',
    fridayHours: 'Friday',
    saturdayHours: 'Saturday',
    sundayHours: 'Sunday',
    totalHours: 'Total Hours',
    billable: 'Billable',
    hasRejections: 'Rejections',
    isOvertime: 'OT',
    overtimeTypeName: 'Overtime Type',
  };

  // Field categories for organization
  const fieldCategories: Record<string, string> = {
    weekEnding: 'Time Period',
    employeeName: 'People',
    department: 'People',
    status: 'Status',
    projectCode: 'Project',
    projectName: 'Project',
    costCentreCode: 'Finance',
    description: 'Details',
    mondayHours: 'Daily Hours',
    tuesdayHours: 'Daily Hours',
    wednesdayHours: 'Daily Hours',
    thursdayHours: 'Daily Hours',
    fridayHours: 'Daily Hours',
    saturdayHours: 'Daily Hours',
    sundayHours: 'Daily Hours',
    totalHours: 'Summary',
    billable: 'Finance',
    hasRejections: 'Status',
    isOvertime: 'Overtime',
    overtimeTypeName: 'Overtime',
  };

  return (
    <DataTableEnhanced
      columns={columns}
      data={data}
      enableGrouping={true}
      enableColumnVisibility={true}
      enableSorting={true}
      enableFiltering={false}
      pageSize={20}
      onRowClick={onRowClick}
      storageKey={isManager ? 'timesheet-report-manager' : 'timesheet-report-employee'}
      fieldLabels={fieldLabels}
      fieldCategories={fieldCategories}
      hideFieldSelector={true}
      initialColumnVisibility={columnVisibility}
      initialGrouping={groupBy || []}
    />
  );
};
